import { Card, CardContent, CardHeader, CardTitle } from "@bcc/components/ui/card";
import type { DataTableRow } from "@client-shared";
import type { CrossBorderAnalytics, PurchaseOrder } from "@shared/types";
import { AlertTriangle, Package } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CrossBorderAnalytics as CrossBorderAnalyticsChart } from "../admin/cross-border-analytics";
import { CrossBorderDirectionList } from "../admin/cross-border-direction-list";
import { CrossBorderErrorAlerts } from "../admin/cross-border-error-alerts";
import { getCrossBorderColumns } from "../admin/cross-border-orders-columns";
import { DataTable } from "../data-table/data-table";
import { setAdminCrossBorderViewTestId } from "./admin-cross-border-view.utils";

interface AdminCrossBorderViewProps {
  readonly orders: PurchaseOrder[];
  readonly analytics?: CrossBorderAnalytics;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly lastUpdatedAt?: Date;
}

export function AdminCrossBorderView({ orders, analytics, isLoading, isError, lastUpdatedAt }: AdminCrossBorderViewProps) {
  const { t } = useTranslation();
  const [directionFilter, setDirectionFilter] = useState<string | null>(null);

  const columns = useMemo(() => getCrossBorderColumns(t), [t]);

  const filteredOrders: DataTableRow<PurchaseOrder>[] = useMemo(() => {
    if (!directionFilter) return orders;
    // Filter on the backend-resolved direction (sourced from the vendor's real
    // ship-from country), not a value re-derived from the BDE region prefix.
    return orders.filter((order) => order.crossBorderDirection === directionFilter);
  }, [orders, directionFilter]);

  const errorCount = analytics?.errorCount ?? 0;

  return (
    <div className="space-y-6" data-testid={setAdminCrossBorderViewTestId("container")}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid={setAdminCrossBorderViewTestId("title")}>
          {t("admin.crossBorder.pageTitle")}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {t("admin.crossBorder.pageDescription")}
          {lastUpdatedAt && (
            <span className="ml-2 text-xs">
              {t("admin.crossBorder.lastUpdated")}: {lastUpdatedAt.toLocaleTimeString()}
            </span>
          )}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card data-testid={setAdminCrossBorderViewTestId("total-card")}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("admin.crossBorder.cards.totalOrders")}</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalOrders ?? 0}</div>
          </CardContent>
        </Card>

        <Card className={errorCount > 0 ? "border-destructive/50" : ""} data-testid={setAdminCrossBorderViewTestId("error-card")}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("admin.crossBorder.cards.errors")}</CardTitle>
            <AlertTriangle className={`h-4 w-4 ${errorCount > 0 ? "text-destructive" : "text-muted-foreground"}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${errorCount > 0 ? "text-destructive" : ""}`}>{errorCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Error Alerts */}
      <CrossBorderErrorAlerts orders={orders} testId={setAdminCrossBorderViewTestId("error-alerts")} />

      {/* Direction Breakdown */}
      <CrossBorderDirectionList
        byDirection={analytics?.byDirection ?? {}}
        totalOrders={analytics?.totalOrders ?? 0}
        activeFilter={directionFilter}
        onFilterChange={setDirectionFilter}
        testId={setAdminCrossBorderViewTestId("direction-list")}
      />

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredOrders}
        isLoading={isLoading}
        isError={isError}
        searchable
        pagination={{ pageSize: 20, pageIndex: 0 }}
        exportable
        exportFilename="cross-border-orders"
      />

      {/* Analytics */}
      <CrossBorderAnalyticsChart orders={orders} testId={setAdminCrossBorderViewTestId("analytics")} />
    </div>
  );
}
